import { useEffect, useRef, type ReactNode } from "react";

const backdrop: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 998,
  backgroundColor: "rgba(0, 0, 0, 0.35)",
};

const container: React.CSSProperties = {
  position: "fixed",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: "200px",
  maxWidth: "90%",
  maxHeight: "80vh",
  overflowY: "auto",
  backgroundColor: "#f2f2f2",
  color: "black",
  zIndex: 999,
  borderRadius: "8px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
  padding: "24px",
};

interface ModalProps {
  children: ReactNode;
  close: () => void;
  label: string;
}

/** A dialog that closes on backdrop click or Escape, and takes focus when opened. */
const Modal = ({ children, close, label }: ModalProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close]);

  return (
    <>
      <div style={backdrop} onClick={close} data-testid="modal-backdrop" />
      <div
        ref={panelRef}
        style={container}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        {children}
      </div>
    </>
  );
};

export default Modal;
